import React, { useEffect, useState, createElement, useRef } from 'react';
import Head from 'next/head';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/router';
import { useData } from 'hooks/useData';
import { useScript } from 'hooks/useScript';
import { components } from './components';
import Text from 'components/3catinfo/Text';
import __isEmpty from 'lodash/isEmpty';
import __isArray from 'lodash/isArray';
import clsx from 'clsx';
import styles from './contingutEstatic.module.scss';

const getFileVersion = (src, version) => {
    return `${src}?v=${version}`;
};

const readHtmlData = (htmlData) => {
    const customComponents = [];

    const parseProps = (props) => {
        const componentProps = {};
        const regex = /(\w+)=("([^"]*)"|'([^']*)'|([^\s]+))/g;
        let match;
        while ((match = regex.exec(props))) {
            const key = match[1];
            const value = match[3] ?? match[4] ?? match[5];

            let finalValue = value;
            if (value === 'true') finalValue = true;
            else if (value === 'false') finalValue = false;
            else if (!isNaN(value)) finalValue = Number(value);

            componentProps[key] = finalValue;
        }
        return componentProps;
    };

    const headContent = htmlData.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';

    const cssInline =
        [...headContent.matchAll(/<link[^>]+rel=["']stylesheet["'][^>]*href=["']([^"']+)["'][^>]*>/gi)].map(
            (match) => match[1]
        ) || [];
    const jsInline =
        [...headContent.matchAll(/<script[^>]+src=["']([^"']+)["'][^>]*>[\s\S]*?<\/script>/gi)].map((match) => match[1]) || [];
    let content = htmlData.replace(/^[\s\S]*<body.*?>/i, '').replace(/<\/body>[\s\S]*$/i, '');

    content = content.replace(/\{\{(.*?)\}\}/g, (_, inside) => {
        const parts = inside.trim();
        const firstSpaceIndex = parts.indexOf(' ');
        const componentName = firstSpaceIndex === -1 ? parts : parts.slice(0, firstSpaceIndex);
        const partProps = firstSpaceIndex === -1 ? '' : parts.slice(firstSpaceIndex + 1);

        if (!componentName) return '';
        const componentProps = parseProps(partProps);
        const componentId = `__${componentName}_${uuidv4()}`;

        customComponents.push({
            id: componentId,
            component: componentName,
            props: componentProps
        });

        return `<div id="${componentId}"></div>`;
    });

    return { content, cssInline, jsInline, customComponents };
};

const refetchScripts = (container) => {
    const scripts = container.current.querySelectorAll('script');

    scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        for (const attr of oldScript.attributes) {
            newScript.setAttribute(attr.name, attr.value);
        }
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
    });
};

const getStyleSheets = (css, versio) => {
    if (__isEmpty(css)) {
        return null;
    }
    return __isArray(css) ? (
        css.map((href) => <link key={`contingut-estatic-css-${uuidv4()}`} rel='stylesheet' href={getFileVersion(href, versio)} />)
    ) : (
        <link rel='stylesheet' href={getFileVersion(css, versio)} />
    );
};

const getJavascripts = (js, versio) => {
    if (__isEmpty(js)) {
        return null;
    }
    return __isArray(js) ? (
        js.map((src) => (
            <script key={`contingut-estatic-javascript-${uuidv4()}`} src={getFileVersion(src, versio)} defer></script>
        ))
    ) : (
        <script src={getFileVersion(js, versio)} defer></script>
    );
};

const ContingutParsejat = (props) => {
    const { contingut } = props;

    const getContent = (content) => {
        return content.map((item, index) => {
            const tipus = item.tipus.toUpperCase();
            if (tipus === 'DIVBLOCK' && !__isEmpty(item.children)) {
                const stylesDiv = clsx({
                    [styles.divBloc]: true,
                    [styles[item.props.estil]]: !!item.props.estil,
                    [item.props.class]: !!item.props.class
                });
                const Tag = item.props.tag || 'div';
                return (
                    <Tag
                        key={`contingut-estatic-divBlock-${uuidv4()}`}
                        className={stylesDiv}
                        {...(item.props.id && { id: item.props.id })}
                    >
                        {getContent(item.children)}
                    </Tag>
                );
            } else {
                return (
                    <React.Fragment key={`contingut-estatic-component-${item.tipus}-${index}`}>
                        {createElement(components[tipus], {
                            ...item.props
                        })}
                    </React.Fragment>
                );
            }
        });
    };

    return <>{getContent(contingut)}</>;
};

const Contingut = (props) => {
    const { contingut } = props;
    const router = useRouter();
    const [renderContingut, setRenderContingut] = useState(<></>);
    const { isJavaScriptClientEnabled } = useScript();

    useEffect(() => {
        if (contingut && !__isArray(contingut)) {
            setRenderContingut(<Text className={styles.cos} text={contingut} />);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.asPath, contingut]);

    return isJavaScriptClientEnabled ? <div>{renderContingut}</div> : <div dangerouslySetInnerHTML={{ __html: contingut }}></div>;
};

const Component3Catinfo = (props) => {
    const { componentDivId } = props;
    const elem = typeof window !== 'undefined' ? document.getElementById(componentDivId) : null;
    if (!elem) return null;

    return ReactDOM.createPortal(props.children, elem);
};

const ContingutEstatic = (props) => {
    const { contingut, assets, estil, html, versio = '1.0.0', resetEstilsGlobals = false } = props;
    const { css, js } = assets || {};
    const isHtmlData = !__isEmpty(html);
    const { data, getRawDades } = useData();
    const [cssList, setCssList] = useState(css || []);
    const [jsList, setJsList] = useState(js || []);
    const [customComponents, setCustomComponents] = useState([]);
    const containerHtmlRef = useRef(null);

    const stylesContingut = clsx({
        [styles.contentContingutEstatic]: true,
        ['estatica-3catinfo']: true,
        ['reset-globals-estatica']: resetEstilsGlobals,
        [styles[estil]]: !!estil
    });

    useEffect(() => {
        if (isHtmlData) {
            getRawDades(getFileVersion(html, versio));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!__isEmpty(data)) {
            const { content, cssInline, jsInline, customComponents } = readHtmlData(data);
            setCssList((prev) => Array.from(new Set([...prev, ...cssInline])));
            setJsList((prev) => Array.from(new Set([...prev, ...jsInline])));

            containerHtmlRef.current.innerHTML = content;
            refetchScripts(containerHtmlRef);
            setCustomComponents(customComponents);
        }
    }, [data]);

    const getContent = () => {
        if (isHtmlData) {
            return (
                <>
                    <div ref={containerHtmlRef}></div>
                    {customComponents.map(({ id, component, props }) => {
                        const Component = components[component.toUpperCase()];
                        if (!Component) return <></>;
                        return (
                            <Component3Catinfo componentDivId={id} key={`contingut-estatic-custom-component-${id}`}>
                                <Component {...props} />
                            </Component3Catinfo>
                        );
                    })}
                </>
            );
        } else {
            return __isArray(contingut) ? <ContingutParsejat contingut={contingut} /> : <Contingut contingut={contingut} />;
        }
    };

    return (
        <>
            <Head>
                {getStyleSheets(cssList, versio)}
                {getJavascripts(jsList, versio)}
            </Head>
            <div className={stylesContingut}>{getContent()}</div>
        </>
    );
};

export default ContingutEstatic;
